import React, { useMemo } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import * as actions from "../../_redux/student-level-section/StudentAssignmentAction";
import {
  DeleteButton,
  ExportButton,
  FormButton,
  copy,
  exportToExcel,
  exportToPDF,
  getAuthorities,
  noRecordsAlert,
  print,
  redirectToPages,
  removalAlert,
  resultAlert
} from "../../../../../_helpers/Helpers";
import { 
  Card, 
  CardBody, 
  CardHeader,
  CardHeaderToolbar,
  ModalProgressBar
} from "../../../../../_metronic/_partials/controls";
import { useStudentLevelSectionUIContext } from "./StudentLevelSectionUIContext";
import { StudentLevelSectionFilter } from "./student-level-section-filter/StudentLevelSectionFilter";
import { StudentLevelSectionTable } from "./student-level-section-table/StudentLevelSectionTable";

export function StudentLevelSectionCard() {
  // StudentLevelSection UI Context
  const studentLevelSectionUIContext = useStudentLevelSectionUIContext();
  const studentLevelSectionUIProps = useMemo(() => {
    return {
      ids: studentLevelSectionUIContext.ids,
      setIds: studentLevelSectionUIContext.setIds,
      queryParams: studentLevelSectionUIContext.queryParams,
      openStudentAssignmentPage: studentLevelSectionUIContext.openStudentAssignmentPage,
    };
  }, [studentLevelSectionUIContext]);
    
  // Getting curret state of Student Level Section list from store (Redux)
  const { currentState } = useSelector(
    (state) => ({ currentState: state.studentLevelSection }),
    shallowEqual
  );
  const { actionsLoading, isSuccess, type, authorities } = currentState;

  // Authority Parameters
  const { isExportationAuthorized, isEditionAuthorized, isDeletionAuthorized } = getAuthorities(authorities);
  
  // Students Redux state
  const dispatch = useDispatch();

  /// Yetki ve Hata Kontrolleri
  if (isSuccess === false) return redirectToPages(type);

  /** Belli Bir Öğrenci ve Kur Sınıfı Listesini Excele Aktarır */
  const exportStudentLevelSectionsToExcel = () => {
    dispatch(actions.getAllStudentAndLevelSections(studentLevelSectionUIProps.queryParams)).then(entities => {
      if (entities.isSuccess === false) return;
      const rows = [];
      const widths = [15, 20, 20, 30, 15, 25, 25];
      const columns = ["Sınıf", "Şube", "Öğrenci Numarası", "Adı Soyadı", "Cinsiyeti", "Kur Sınıfı", "Kur Dersi"];
      
      entities.forEach(entity => rows.push([
        entity.grade,
        entity.section,
        entity.studentNumber,
        entity.studentName,
        entity.sexName,
        entity.levelSection,
        entity.levelCourse
      ]));
      
      const filename = studentLevelSectionUIProps.queryParams.term + " - " + (studentLevelSectionUIProps.queryParams.term + 1) + " Eğitim Öğretim Yılı Öğrenci ve Kur Sınıfı Listesi";
      const sheetname = "Öğrenci ve Kur Sınıfı Listesi";
      exportToExcel({ columns, rows, widths, filename, sheetname });
    });
  }

  /** Belli Bir Öğrenci ve Kur Sınıfı Listesini PDF e Aktarır */
  const exportStudentLevelSectionsToPDF = () => {
    dispatch(actions.getAllStudentAndLevelSections(studentLevelSectionUIProps.queryParams)).then(entities => {
      if (entities.isSuccess === false) return;
      const views = [ 
        {
          header: "Sınıf",
          rows: entities.map(entity => entity.grade)
        },
        {
          header: "Şube",
          rows: entities.map(entity => entity.section)
        },
        {
          header: "Öğrenci Numarası",
          rows: entities.map(entity => entity.studentNumber)
        },
        {
          header: "Adı Soyadı",
          rows: entities.map(entity => entity.studentName)
        },
        {
          header: "Cinsiyeti",
          rows: entities.map(entity => entity.sexName)
        },
        {
          header: "Kur Sınıfı",
          rows: entities.map(entity => entity.levelSection)
        },
        {
          header: "Kur Dersi",
          rows: entities.map(entity => entity.levelCourse)
        }
      ];
  
      const filename = studentLevelSectionUIProps.queryParams.term + " - " + (studentLevelSectionUIProps.queryParams.term + 1) + " Eğitim Öğretim Yılı Öğrenci ve Kur Sınıfı Listesi";
      exportToPDF(views, filename);
    });
  }

  /** Belli Bir Öğrenci ve Kur Sınıfı Listesini Yazdırma Penceresini Açar */
  const printStudentLevelSections = () => {
    dispatch(actions.getAllStudentAndLevelSections(studentLevelSectionUIProps.queryParams)).then(entities => {
      if (entities.isSuccess === false) return;
      const data = [];    
      const header = studentLevelSectionUIProps.queryParams.term + " - " + (studentLevelSectionUIProps.queryParams.term + 1) + " Eğitim Öğretim Yılı Öğrenci ve Kur Sınıfı Listesi";
      const properties = ["Sınıf", "Şube", "Öğrenci Numarası", "Adı Soyadı", "Cinsiyeti", "Kur Sınıfı", "Kur Dersi"];
  
      entities.forEach(entity => {
        data.push({
          "Sınıf": entity.grade,
          "Şube": entity.section ? entity.section : "",
          "Öğrenci Numarası": entity.studentNumber,
          "Adı Soyadı": entity.studentName,
          "Cinsiyeti": entity.sexName,
          "Kur Sınıfı": entity.levelSection,
          "Kur Dersi": entity.levelCourse
        });
      });
  
      print({ data, properties, header });
    });    
  }

  /** Belli Bir Öğrenci ve Kur Sınıfı Listesini Panoya Kopyalar */
  const copyStudentLevelSections = () => {
    dispatch(actions.getAllStudentAndLevelSections(studentLevelSectionUIProps.queryParams)).then(entities => {
      if (entities.isSuccess === false) return;
      const rows = [];
      const columns = ["Sınıf", "Şube", "Öğrenci Numarası", "Adı Soyadı", "Cinsiyeti", "Kur Sınıfı", "Kur Dersi"];
      const header = studentLevelSectionUIProps.queryParams.term + " - " + (studentLevelSectionUIProps.queryParams.term + 1) + " Eğitim Öğretim Yılı Öğrenci ve Kur Sınıfı Listesi";
      entities.forEach(entity => rows.push([
        entity.grade,
        entity.section ? entity.section : "",
        entity.studentNumber,
        entity.studentName,
        entity.sexName,
        entity.levelSection,
        entity.levelCourse
      ]));
      copy({ header, columns, rows });
    });
  }

  /** Belli Bir Öğrenci Listesini İlgili Kur Sınıflarından Çıkarır */
  const removeStudents = (ids) => {
    if (typeof ids === 'string') ids = ids.split(",");
  
    if (ids.length < 1) {
      noRecordsAlert();
      return;
    }

    const message = "İlgili öğrenci(ler) kur sınıfı(ları)ndan çıkarılacaktır."
    removalAlert(message).then(result => {
      if (result.isConfirmed) dispatch(actions.removeStudents(ids)).then(result => onResult(result));
    });
  }

  const onResult = ({ isSuccess, type, message }) => {
    resultAlert(type, message);
  
    if (isSuccess) {
      setTimeout(() => {
        studentLevelSectionUIProps.setIds([]);
        dispatch(actions.getStudentAndLevelSections(studentLevelSectionUIProps.queryParams));
      }, 2500);
    }
  };
  
  const title = <>Öğrenci ve Kur Sınıfı Listesi{studentLevelSectionUIProps.ids.length > 0 ?
                  <> [Seçilen Kayıt Sayısı: <span style={{color:"red"}}>{studentLevelSectionUIProps.ids.length}</span>]</> :
                  ""}
                </>;

  return (
    <Card>
      {actionsLoading && <ModalProgressBar />}
      <CardHeader title={title}>
        {
          (isExportationAuthorized || isEditionAuthorized || isDeletionAuthorized) &&
          <CardHeaderToolbar>
            {
              isExportationAuthorized &&
              <ExportButton
                exportToExcel={exportStudentLevelSectionsToExcel}
                exportToPDF={exportStudentLevelSectionsToPDF}
                print={printStudentLevelSections}
                copy={copyStudentLevelSections}
              />
            }
            {
              isEditionAuthorized &&
              <FormButton 
                path="Navigation/Check"
                title="Kur Sınıfına Öğrenci Atama"
                method={studentLevelSectionUIProps.openStudentAssignmentPage}
              />
            }
            {
              isDeletionAuthorized &&
              <DeleteButton
                path="Navigation/Close"
                title="Seçilenleri Çıkar"
                method={() => removeStudents(studentLevelSectionUIProps.ids)}
              />
            }
          </CardHeaderToolbar>
        }        
      </CardHeader>
      <CardBody>
        <StudentLevelSectionFilter />
        <StudentLevelSectionTable
          removeStudents={removeStudents}
        />
      </CardBody>
    </Card>
  );
}